import React, { useState } from "react";
import { Box, Typography, IconButton, Divider, Button } from "@mui/material";
import { Add, ModeEditOutlineTwoTone, DeleteTwoTone } from "@mui/icons-material";
import { toast } from "react-toastify";
import FormDrawer from "components/Shared/FormDrawer";
import ShipmentItemsForm from "./ShipmentItemsForm";
import RecordCard from "./RecordCard";
import { useShipmentDetailContext } from "contexts/ShipmentDetailContext";
import { useAuth } from "hooks";
import { useUpdateShipmentMutation } from "store/services/shipmentApi";

const FieldRow = ({ label, value }) => (
    <Box sx={{ display: "flex", py: 0.25 }}>
        <Box sx={{ width: "40%", color: "text.primary", fontSize: "0.7rem", fontWeight: 600 }}>{label}</Box>
        <Box sx={{ width: "60%", fontSize: "0.74rem", fontWeight: 500 }}>{value ?? "N/A"}</Box>
    </Box>
);

const ItemRecordCard = ({ row, index, canShowActions, onEdit, onDelete }) => (
    <RecordCard>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Typography sx={{ fontSize: "0.78rem", fontWeight: 600 }}>{row?.name || "N/A"}</Typography>
            {canShowActions && (
                <Box>
                    <IconButton size="small" onClick={() => onEdit(row, index)}>
                        <ModeEditOutlineTwoTone sx={{ fontSize: "1rem" }} />
                    </IconButton>
                    <IconButton size="small" color="error" onClick={() => onDelete(index)}>
                        <DeleteTwoTone sx={{ fontSize: "1rem" }} />
                    </IconButton>
                </Box>
            )}
        </Box>
        <Divider sx={{ my: 0.5 }} />
        <FieldRow label="Item Reference" value={row?.itemReference} />
        <FieldRow label="Cargo Type" value={row?.cargoType?.name} />
        <FieldRow label="Cargo Condition" value={row?.cargoCondition?.name} />
        <FieldRow label="Weight" value={row?.weight ? `${row.weight.toFixed(2)} kg` : "N/A"} />
        <FieldRow
            label="Dimensions"
            value={
                row?.dimensions
                    ? `${row.dimensions.length?.toFixed(2) || 0} x ${row.dimensions.width?.toFixed(2) || 0} x ${row.dimensions.height?.toFixed(2) || 0} m`
                    : "N/A"
            }
        />
    </RecordCard>
);

const ShipmentItemsCompact = () => {
    const { isCustomer, isVendor, isContractor, isDriver, isVendorStaff } = useAuth();
    // Matches ShipmentItemsTab's Add-button gate (includes isVendorStaff)
    const hideAddButton = isCustomer || isVendor || isContractor || isDriver || isVendorStaff;
    // Matches ShipmentItemsList's Edit/Delete gate (excludes isVendorStaff) — preserved as-is from original
    const showActions = !(isCustomer || isVendor || isContractor || isDriver);

    const { ShipmentDetail } = useShipmentDetailContext();
    const [showItemForm, setShowItemForm] = useState(false);
    const [selectedItem, setSelectedItem] = useState(null);
    const [updateShipment, { isLoading: updatingShipment }] = useUpdateShipmentMutation();

    const items = Array.isArray(ShipmentDetail?.shipmentDetails) ? ShipmentDetail.shipmentDetails : [];
    const sortedItems = React.useMemo(() => {
        if (items.length > 0 && items[0]?.createdAt) {
            return [...items].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        }
        return [...items].reverse();
    }, [items]);

    const handleCancelItem = () => {
        setShowItemForm(false);
        setSelectedItem(null);
    };

    const handleSubmitItem = () => {
        setShowItemForm(false);
        setSelectedItem(null);
    };

    const handleEdit = (row, index) => {
        setSelectedItem({ ...row, index });
        setShowItemForm(true);
    };

    // NOTE: preserves original bug — `index` is the position in sortedItems,
    // but filter() below runs against the unsorted ShipmentDetail.shipmentDetails array.
    const handleDelete = async (index) => {
        try {
            const updatedDetails = ShipmentDetail.shipmentDetails.filter((_, i) => i !== index);
            await updateShipment({
                _id: ShipmentDetail._id,
                shipmentDetails: updatedDetails,
            }).unwrap();
            toast.success("Item deleted successfully!");
        } catch (err) {
            toast.error(err?.data?.message || "Failed to delete item!");
        }
    };

    return (
        <Box sx={{ p: 1 }}>
            {!hideAddButton && (
                <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1 }}>
                    <Button
                        size="small"
                        startIcon={<Add sx={{ fontSize: "0.9rem" }} />}
                        variant="outlined"
                        disabled={ShipmentDetail?.isActive === false}
                        onClick={() => { setSelectedItem(null); setShowItemForm(true); }}
                        sx={{ fontSize: "0.68rem", py: 0.25 }}
                    >
                        Add Item
                    </Button>
                </Box>
            )}
            {sortedItems.length === 0 ? (
                <Typography sx={{ fontSize: "0.74rem", color: "text.secondary", textAlign: "center", py: 2 }}>
                    No shipment items found.
                </Typography>
            ) : (
                sortedItems.map((row, index) => (
                    <ItemRecordCard
                        key={row._id || index}
                        row={row}
                        index={index}
                        canShowActions={showActions}
                        onEdit={handleEdit}
                        onDelete={handleDelete}
                    />
                ))
            )}
            <FormDrawer title={selectedItem ? "Edit Item" : "Add Item"} open={showItemForm} onClose={handleCancelItem}>
                <ShipmentItemsForm
                    isBusy={updatingShipment}
                    item={selectedItem}
                    shipmentId={ShipmentDetail?._id}
                    onSubmit={handleSubmitItem}
                    onCancel={handleCancelItem}
                />
            </FormDrawer>
        </Box>
    );
};

export default ShipmentItemsCompact;